const loaderScript = Array.from(document.scripts).find((script) => script.src === import.meta.url);

if (loaderScript instanceof HTMLScriptElement) {
  const sectionId = loaderScript.dataset.sectionId;
  const searchUrl = loaderScript.dataset.searchUrl || '/search';
  const predictiveSearchSrc = loaderScript.dataset.predictiveSearchSrc;
  const quickAddSrc = loaderScript.dataset.quickAddSrc;
  const desktopQuery = window.matchMedia('(min-width: 750px)');
  let overlayPromise;
  let replayingClick = false;

  const importModule = (src) => (src ? import(src) : Promise.resolve());

  const activateScripts = (root) => {
    for (const oldScript of root.querySelectorAll('script')) {
      const script = document.createElement('script');

      for (const { name, value } of oldScript.attributes) {
        script.setAttribute(name, value);
      }

      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    }
  };

  const getSectionUrl = () => {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('section_id', sectionId || 'ohsio-lazy-overlays');
    return url;
  };

  const loadOverlays = () => {
    if (document.querySelector('[data-ohsio-lazy-overlays]')) return Promise.resolve();
    if (overlayPromise) return overlayPromise;

    overlayPromise = fetch(getSectionUrl(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Overlay section request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section =
          parsed.getElementById(`shopify-section-${sectionId}`) ||
          parsed.querySelector('[data-ohsio-lazy-overlays]')?.parentElement;

        if (!(section instanceof HTMLElement)) {
          throw new Error('Overlay section markup was not found.');
        }

        const importedSection = document.importNode(section, true);

        for (const stylesheet of parsed.querySelectorAll(
          'style[data-section-stylesheet], link[rel="stylesheet"][href*="compiled_assets"]'
        )) {
          if (!section.contains(stylesheet)) {
            importedSection.prepend(document.importNode(stylesheet, true));
          }
        }

        document.body.appendChild(importedSection);
        activateScripts(importedSection);
      })
      .catch((error) => {
        overlayPromise = undefined;
        console.error('Unable to load storefront overlays.', error);
        throw error;
      });

    return overlayPromise;
  };

  const openSearch = async () => {
    await Promise.all([loadOverlays(), importModule(predictiveSearchSrc)]);
    await Promise.all([
      customElements.whenDefined('dialog-component'),
      customElements.whenDefined('predictive-search-component'),
    ]);

    const modal = document.getElementById('search-modal');
    if (!modal || typeof modal.showDialog !== 'function') return;

    modal.showDialog();
    requestAnimationFrame(() => modal.querySelector('input[type="search"]')?.focus({ preventScroll: true }));
  };

  const prepareQuickAdd = () => Promise.all([loadOverlays(), importModule(quickAddSrc)]);

  const findIntent = (target) => {
    if (!(target instanceof Element)) return null;

    const searchButton = target.closest('search-button button');
    if (searchButton) return { type: 'search', trigger: searchButton };

    const quickAddButton = target.closest('quick-add-component .quick-add__button--choose');
    if (quickAddButton) return { type: 'quick-add', trigger: quickAddButton };

    return null;
  };

  const warmOnIntent = (event) => {
    const intent = findIntent(event.target);
    if (!intent) return;

    if (intent.type === 'search') {
      Promise.all([loadOverlays(), importModule(predictiveSearchSrc)]).catch(() => {});
    } else {
      prepareQuickAdd().catch(() => {});
    }
  };

  document.addEventListener('pointerover', warmOnIntent, true);
  document.addEventListener('pointerdown', warmOnIntent, true);
  document.addEventListener('focusin', warmOnIntent, true);

  document.addEventListener(
    'click',
    async (event) => {
      if (replayingClick) return;

      const intent = findIntent(event.target);
      if (!intent) return;

      if (intent.type === 'search' && !document.getElementById('search-modal')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        await openSearch().catch(() => window.location.assign(searchUrl));
        return;
      }

      if (intent.type === 'quick-add' && !document.getElementById('quick-add-dialog')) {
        event.preventDefault();
        event.stopImmediatePropagation();

        try {
          await prepareQuickAdd();
          await Promise.all([
            customElements.whenDefined('quick-add-component'),
            customElements.whenDefined('quick-add-dialog'),
          ]);
          replayingClick = true;
          intent.trigger.click();
        } catch (_) {
          const productLink = intent.trigger.closest('product-card')?.querySelector('a[href*="/products/"]');
          if (productLink instanceof HTMLAnchorElement) window.location.assign(productLink.href);
        } finally {
          replayingClick = false;
        }
      }
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (!event.metaKey || event.key.toLowerCase() !== 'k') return;
      if (document.getElementById('search-modal')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      openSearch().catch(() => window.location.assign(searchUrl));
    },
    true
  );

  const loadWhenDesktopIsIdle = () => {
    if (!desktopQuery.matches) return;

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => loadOverlays().catch(() => {}), { timeout: 3000 });
    } else {
      window.setTimeout(() => loadOverlays().catch(() => {}), 1500);
    }
  };

  if (document.readyState === 'complete') {
    loadWhenDesktopIsIdle();
  } else {
    window.addEventListener('load', loadWhenDesktopIsIdle, { once: true });
  }
}
